import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Sparkles, Loader2, Check, X, Wand2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyProduct = () => ({
  product_name: "",
  active_ingredient: "",
  target_diseases: "",
  dose: "",
  quantity_used: "",
  carenza_days: "",
});

export default function AIJournalInput({ vineyards, onCreated }) {
  const { t, i18n } = useTranslation();
  const EXAMPLES = t("aiJournal.examples", { returnObjects: true });
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({});

  const reset = () => {
    setText("");
    setParsed(null);
    setEdit({});
    setOpen(false);
  };

  const onParse = async () => {
    if (!text.trim()) { toast.error(t("aiJournal.emptyTextError")); return; }
    if (vineyards.length === 0) { toast.error(t("aiJournal.noVineyardError")); return; }
    setParsing(true);
    setParsed(null);
    try {
      const r = await axios.post(`${API}/journal/parse`, { text, language: i18n.language }, { withCredentials: true, timeout: 60000 });
      const data = r.data;
      if (data.kind === "unknown") {
        toast.warning(t("aiJournal.unclearError"));
        return;
      }
      if (!data.vineyard_ids || data.vineyard_ids.length === 0) {
        data.vineyard_ids = vineyards.map((v) => v.id);
      }
      setParsed(data);

      if (data.kind === "treatment") {
        const productsRaw = (data.products && data.products.length)
          ? data.products
          : [{
              product_name: data.product_name,
              active_ingredient: data.active_ingredient,
              target_diseases: data.target_diseases,
              dose: data.dose,
              quantity_used: data.quantity_used,
              carenza_days: data.carenza_days,
            }];
        const products = productsRaw.map((p) => ({
          product_name: p.product_name || "",
          active_ingredient: p.active_ingredient || "",
          target_diseases: Array.isArray(p.target_diseases)
            ? p.target_diseases.join(", ")
            : (p.target_diseases || ""),
          dose: p.dose || "",
          quantity_used: p.quantity_used || "",
          carenza_days: p.carenza_days != null && p.carenza_days !== "" ? String(p.carenza_days) : "",
        }));
        setEdit({
          vineyard_ids: data.vineyard_ids,
          treatment_date: data.treatment_date || new Date().toISOString().slice(0, 10),
          surface_treated: data.surface_treated != null ? String(data.surface_treated) : "",
          operator_name: data.operator_name || "",
          notes: data.notes || "",
          products,
        });
      } else {
        // fertilization
        setEdit({
          vineyard_ids: data.vineyard_ids,
          product_name: data.product_name || "",
          treatment_date: data.treatment_date || new Date().toISOString().slice(0, 10),
          quantity_used: data.quantity_used || "",
          surface_treated: data.surface_treated != null ? String(data.surface_treated) : "",
          operator_name: data.operator_name || "",
          product_type: data.product_type || "",
          application_method: data.application_method || "",
          objective: data.objective || "",
          notes: data.notes || "",
        });
      }
    } catch (e) {
      toast.error(t("aiJournal.parseError"));
    } finally { setParsing(false); }
  };

  const toggleVineyard = (vid) => {
    setEdit((e) => ({
      ...e,
      vineyard_ids: e.vineyard_ids.includes(vid)
        ? e.vineyard_ids.filter((x) => x !== vid)
        : [...e.vineyard_ids, vid],
    }));
  };

  const updateProduct = (idx, field, value) => {
    setEdit((e) => ({
      ...e,
      products: e.products.map((p, i) => i === idx ? { ...p, [field]: value } : p),
    }));
  };

  const addProduct = () => {
    setEdit((e) => ({ ...e, products: [...(e.products || []), emptyProduct()] }));
  };

  const removeProduct = (idx) => {
    setEdit((e) => ({
      ...e,
      products: e.products.length === 1 ? e.products : e.products.filter((_, i) => i !== idx),
    }));
  };

  const save = async () => {
    if (!parsed || !edit.vineyard_ids?.length) {
      toast.error(t("aiJournal.selectVineyardError"));
      return;
    }
    setSaving(true);
    try {
      const [primary, ...additional] = edit.vineyard_ids;
      if (parsed.kind === "treatment") {
        const validProducts = (edit.products || []).filter((p) => p.product_name.trim());
        if (validProducts.length === 0) {
          toast.error(t("aiJournal.addProductError"));
          setSaving(false);
          return;
        }
        for (const p of validProducts) {
          const payload = {
            vineyard_id: primary,
            additional_vineyard_ids: additional,
            treatment_date: edit.treatment_date,
            product_name: p.product_name,
            active_ingredient: p.active_ingredient || null,
            target_diseases: p.target_diseases.split(",").map((s) => s.trim()).filter(Boolean),
            dose: p.dose || null,
            quantity_used: p.quantity_used || null,
            surface_treated: edit.surface_treated ? parseFloat(edit.surface_treated) : null,
            carenza_days: p.carenza_days ? parseInt(p.carenza_days) : null,
            operator_name: edit.operator_name || null,
            notes: edit.notes || null,
          };
          await axios.post(`${API}/treatments`, payload, { withCredentials: true });
        }
        toast.success(t("aiJournal.treatmentSaved", { count: validProducts.length }));
      } else if (parsed.kind === "fertilization") {
        if (!edit.product_name?.trim()) {
          toast.error(t("aiJournal.fertilizerNameError"));
          setSaving(false);
          return;
        }
        const payload = {
          vineyard_id: primary,
          additional_vineyard_ids: additional,
          fertilization_date: edit.treatment_date,
          product_name: edit.product_name,
          product_type: edit.product_type || null,
          quantity_used: edit.quantity_used || null,
          surface_treated: edit.surface_treated ? parseFloat(edit.surface_treated) : null,
          operator_name: edit.operator_name || null,
          application_method: edit.application_method || null,
          objective: edit.objective || null,
          notes: edit.notes || null,
        };
        await axios.post(`${API}/fertilizations`, payload, { withCredentials: true });
        toast.success(t("aiJournal.fertilizationSaved"));
      }
      onCreated?.();
      reset();
    } catch (e) {
      toast.error(t("aiJournal.saveError"));
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="vs-btn-primary w-full inline-flex items-center justify-center gap-2 mb-4"
        style={{ background: "var(--gradient-warm)", borderColor: "var(--gold)", color: "white" }}
        data-testid="ai-journal-open">
        <Wand2 size={18} /> {t("aiJournal.openButton")}
      </button>
    );
  }

  return (
    <div className="vs-card p-4 mb-4" style={{ borderColor: "var(--gold)", borderWidth: 2 }} data-testid="ai-journal">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={20} style={{ color: "var(--gold)" }} />
          <h3 className="heading font-bold">{t("aiJournal.title")}</h3>
        </div>
        <button onClick={reset} className="p-1"><X size={18} /></button>
      </div>

      {!parsed ? (
        <>
          <p className="text-sm mb-2" style={{ color: "var(--text-secondary)" }}>
            {t("aiJournal.instructionPrefix")} <strong>{t("aiJournal.instructionBold")}</strong> {t("aiJournal.instructionSuffix")}
          </p>
          <textarea
            className="vs-input mb-2"
            rows="3"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("aiJournal.textareaPlaceholder")}
            data-testid="ai-journal-text"
          />
          <div className="flex flex-wrap gap-1 mb-3">
            <span className="text-xs font-bold" style={{ color: "var(--text-muted)" }}>{t("aiJournal.examplesLabel")}</span>
            {EXAMPLES.map((ex, i) => (
              <button key={i} onClick={() => setText(ex)}
                className="text-xs px-2 py-1 rounded"
                style={{ background: "var(--secondary)", border: "1px solid var(--border)" }}
                data-testid={`example-${i}`}>
                {ex.slice(0, 35)}...
              </button>
            ))}
          </div>
          <button onClick={onParse} disabled={parsing}
            className="vs-btn-primary w-full inline-flex items-center justify-center gap-2"
            style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}
            data-testid="ai-journal-parse">
            {parsing ? <><Loader2 size={18} className="animate-spin" /> {t("aiJournal.parseButtonLoading")}</> : <><Wand2 size={18} /> {t("aiJournal.parseButtonLabel")}</>}
          </button>
        </>
      ) : (
        <>
          <div className="p-2 rounded mb-3 text-xs" style={{ background: "var(--secondary)" }}>
            ✨ {t("aiJournal.recognizedLabel")} <strong>
              {parsed.kind === "treatment"
                ? t("aiJournal.treatmentRecognized", { count: edit.products?.length || 0 })
                : t("aiJournal.fertilizationRecognized")}
            </strong>
          </div>

          {/* Vineyards multi-select */}
          <div className="mb-3">
            <div className="vs-label">{t("aiJournal.vineyardsAppliedLabel", { count: edit.vineyard_ids.length })}</div>
            <div className="flex flex-wrap gap-1.5">
              {vineyards.map((v) => {
                const sel = edit.vineyard_ids.includes(v.id);
                return (
                  <button key={v.id} onClick={() => toggleVineyard(v.id)}
                    className="px-3 py-1.5 rounded-lg text-sm font-semibold"
                    style={{
                      background: sel ? "var(--primary)" : "var(--surface)",
                      color: sel ? "white" : "var(--text-primary)",
                      border: `2px solid ${sel ? "var(--primary)" : "var(--border)"}`,
                    }}
                    data-testid={`ai-vineyard-${v.id}`}>
                    {sel ? "✓ " : ""}{v.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Shared fields */}
          <div className="space-y-2 mb-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="vs-label">{t("aiJournal.dateLabel")}</div>
                <input type="date" className="vs-input" value={edit.treatment_date}
                  onChange={(e) => setEdit({ ...edit, treatment_date: e.target.value })} />
              </div>
              <div>
                <div className="vs-label">{t("aiJournal.totalSurfaceLabel")}</div>
                <input type="number" step="0.01" className="vs-input" value={edit.surface_treated}
                  onChange={(e) => setEdit({ ...edit, surface_treated: e.target.value })} />
              </div>
            </div>
            <div>
              <div className="vs-label">{t("aiJournal.operatorLabel")}</div>
              <input className="vs-input" value={edit.operator_name}
                onChange={(e) => setEdit({ ...edit, operator_name: e.target.value })}
                placeholder={t("aiJournal.operatorPlaceholder")} />
            </div>
          </div>

          {/* Products list (only for treatments) */}
          {parsed.kind === "treatment" && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="vs-label" style={{ marginBottom: 0 }}>
                  {t("aiJournal.productsCountLabel", { count: edit.products?.length || 0 })}
                </div>
                <button onClick={addProduct}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-semibold"
                  style={{ background: "var(--primary)", color: "white" }}
                  data-testid="ai-add-product">
                  <Plus size={14} /> {t("aiJournal.addButton")}
                </button>
              </div>
              <div className="space-y-3">
                {(edit.products || []).map((p, idx) => (
                  <div key={idx} className="p-3 rounded-lg"
                    style={{ background: "var(--secondary)", border: "1px solid var(--border)" }}
                    data-testid={`ai-product-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold" style={{ color: "var(--text-muted)" }}>
                        {t("aiJournal.productNumberLabel", { num: idx + 1 })}
                      </span>
                      {edit.products.length > 1 && (
                        <button onClick={() => removeProduct(idx)} className="p-1"
                          data-testid={`ai-remove-product-${idx}`}>
                          <Trash2 size={14} style={{ color: "#c00" }} />
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div>
                        <div className="vs-label">{t("aiJournal.productNameLabel")}</div>
                        <input className="vs-input" value={p.product_name}
                          onChange={(e) => updateProduct(idx, "product_name", e.target.value)}
                          placeholder={t("aiJournal.productNamePlaceholder")} />
                      </div>
                      <div>
                        <div className="vs-label">{t("aiJournal.activeIngredientLabel")}</div>
                        <input className="vs-input" value={p.active_ingredient}
                          onChange={(e) => updateProduct(idx, "active_ingredient", e.target.value)}
                          placeholder={t("aiJournal.activeIngredientPlaceholder")} />
                      </div>
                      <div>
                        <div className="vs-label">{t("aiJournal.targetDiseasesLabel")}</div>
                        <input className="vs-input" value={p.target_diseases}
                          onChange={(e) => updateProduct(idx, "target_diseases", e.target.value)}
                          placeholder={t("aiJournal.targetDiseasesPlaceholder")} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="vs-label">{t("aiJournal.doseLabel")}</div>
                          <input className="vs-input" value={p.dose}
                            onChange={(e) => updateProduct(idx, "dose", e.target.value)}
                            placeholder={t("aiJournal.dosePlaceholder")} />
                        </div>
                        <div>
                          <div className="vs-label">{t("aiJournal.quantityLabel")}</div>
                          <input className="vs-input" value={p.quantity_used}
                            onChange={(e) => updateProduct(idx, "quantity_used", e.target.value)}
                            placeholder={t("aiJournal.quantityPlaceholder")} />
                        </div>
                      </div>
                      <div>
                        <div className="vs-label">{t("aiJournal.carenzaLabel")}</div>
                        <input type="number" className="vs-input" value={p.carenza_days}
                          onChange={(e) => updateProduct(idx, "carenza_days", e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fertilization single fields */}
          {parsed.kind === "fertilization" && (
            <div className="space-y-2 mb-3">
              <div>
                <div className="vs-label">{t("aiJournal.fertilizerLabel")}</div>
                <input className="vs-input" value={edit.product_name}
                  onChange={(e) => setEdit({ ...edit, product_name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="vs-label">{t("aiJournal.typeLabel")}</div>
                  <input className="vs-input" value={edit.product_type}
                    onChange={(e) => setEdit({ ...edit, product_type: e.target.value })} />
                </div>
                <div>
                  <div className="vs-label">{t("aiJournal.quantityLabel")}</div>
                  <input className="vs-input" value={edit.quantity_used}
                    onChange={(e) => setEdit({ ...edit, quantity_used: e.target.value })}
                    placeholder={t("aiJournal.fertQuantityPlaceholder")} />
                </div>
              </div>
              <div>
                <div className="vs-label">{t("aiJournal.methodLabel")}</div>
                <input className="vs-input" value={edit.application_method}
                  onChange={(e) => setEdit({ ...edit, application_method: e.target.value })} />
              </div>
              <div>
                <div className="vs-label">{t("aiJournal.objectiveLabel")}</div>
                <input className="vs-input" value={edit.objective}
                  onChange={(e) => setEdit({ ...edit, objective: e.target.value })} />
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={() => setParsed(null)} className="vs-btn-secondary flex-1" disabled={saving}>
              {t("aiJournal.rewriteButton")}
            </button>
            <button onClick={save} disabled={saving}
              className="vs-btn-primary flex-1 inline-flex items-center justify-center gap-2"
              style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}
              data-testid="ai-journal-save">
              <Check size={16} /> {saving ? t("aiJournal.saving") : t("aiJournal.saveButton")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
